import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const AboutContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-turquoise);
  }
`;

const IntroText = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const Section = styled.section`
  margin-bottom: var(--spacing-xxl);
`;

const SectionTitle = styled.h2`
  margin-bottom: var(--spacing-md);
  color: var(--accent-lavender);
`;

const SectionContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const SectionText = styled.div``;

const SectionImage = styled.div`
  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 968px) {
    order: -1;
  }
`;

const FounderSection = styled.div`
  background-color: var(--secondary);
  border-radius: 8px;
  padding: var(--spacing-xl);
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xxl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FounderImage = styled.div`
  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FounderBio = styled.div`
  h3 {
    color: var(--accent-lavender);
    margin-bottom: var(--spacing-sm);
  }
  
  h4 {
    color: var(--text-light);
    font-weight: normal;
    margin-bottom: var(--spacing-md);
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  margin-top: var(--spacing-lg);
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  
  h3 {
    color: var(--accent-turquoise);
    margin-bottom: var(--spacing-sm);
  }
`;

const ServiceButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: auto;
  align-self: flex-start;
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const ClientTypes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-lg);
  margin-top: var(--spacing-lg);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ClientCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  
  h3 {
    color: var(--accent-lavender);
    margin-bottom: var(--spacing-sm);
  }
`;

const CTASection = styled.div`
  background-color: var(--accent-lavender);
  border-radius: 8px;
  padding: var(--spacing-xl);
  text-align: center;
  color: white;
  
  h2 {
    margin-bottom: var(--spacing-md);
  }
  
  p {
    max-width: 800px;
    margin: 0 auto var(--spacing-lg);
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--accent-lavender);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid white;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: white;
  }
`;

const About = () => {
  return (
    <AboutContainer>
      <Container>
        <PageTitle>About <span>Passport Creative</span></PageTitle>
        <IntroText>
          A boutique travel marketing studio creating innovative solutions for forward-thinking travel brands.
        </IntroText>
        
        <Section>
          <SectionContent>
            <SectionText>
              <SectionTitle>Our Story</SectionTitle>
              <p>Passport Creative was born from a passion for travel innovation and a desire to help exceptional travel brands tell their stories more effectively. Based in Paris but working globally, we bring a unique international perspective to every project.</p>
              <p>We believe the travel industry deserves more strategic, creative solutions that rise above the noise of typical destination marketing. Our approach blends data-driven insights with creative storytelling to create memorable experiences that drive real business results.</p>
            </SectionText>
          </SectionContent>
        </Section>
        
        <FounderSection>
          <FounderImage>
            <img src="/images/about/alexjeffries.jpg" alt="Founder Portrait" />
          </FounderImage>
          <FounderBio>
            <h3>Meet the Founder</h3>
            <h4>Alex Jeffries | Creative Director & Strategist</h4>
            <p>My professional career started with answering phones for Carson Daly and doing research for Ellen DeGeneres before producing the award-winning talk show The Real. Then in 2020, I moved to Paris, France to pursue travel, tourism, and marketing.</p>
            <p>With a passion for trail running and travel, I bring a unique perspective to every project. I'm particularly focused on sustainable tourism development and creating digital tools that solve real problems in the travel ecosystem.</p>
            <p>When not working with clients, you'll find me exploring new trail routes, testing travel tech, and brewing fresh coffee for visitors to Paris.</p>
          </FounderBio>
        </FounderSection>
        
        <Section>
          <SectionTitle>What We Do</SectionTitle>
          <p>At Passport Creative, we provide three core service areas designed to help travel brands grow and innovate:</p>
          
          <ServiceGrid>
            <ServiceCard>
              <h3>Travel Marketing</h3>
              <p>From helping tour operators to travel businesses, the goal is the same: create marketing that reaches the right kind of traveler. That includes website development, email marketing, content strategy, and SEO designed to support businesses with a long view.</p>
              <ServiceButton to="/services/travel-marketing">Learn More</ServiceButton>
            </ServiceCard>
            
            <ServiceCard>
              <h3>Tourism Development</h3>
              <p>Passport Creative has expertise in tourism development across multiple continents, from coffee tourism research in Uganda to national tourism policy development in Sri Lanka. With experience working with both governmental organizations and private sector clients in Africa, Asia, and Europe, Passport Creative brings a global perspective to tourism development projects.</p>
              <ServiceButton to="/services/tourism-development">Learn More</ServiceButton>
            </ServiceCard>
            
            <ServiceCard>
              <h3>Project Incubation</h3>
              <p>Passport Creative develops digital tools to solve specific challenges in the travel industry. This includes atraversa, a destination discovery platform, and specialized systems for planning familiarization trips that help tour operators and destinations gain better market access. Each tool is designed with both business needs and traveler experience in mind.</p>
              <ServiceButton to="/services/digital-tools">Learn More</ServiceButton>
            </ServiceCard>
          </ServiceGrid>
        </Section>
        
        <Section>
          <SectionTitle>Who We Work With</SectionTitle>
          <p>We partner with forward-thinking travel businesses who understand that exceptional marketing and innovative tools are investments in long-term growth, not expenses. Our clients typically include:</p>
          
          <ClientTypes>
            <ClientCard>
              <h3>Boutique Accommodations</h3>
              <p>Independent hotels, eco-lodges, and unique properties that want to showcase their distinctive character and attract the right guests who value their experience.</p>
            </ClientCard>
            
            <ClientCard>
              <h3>Destination Management Organizations</h3>
              <p>Tourism boards and DMOs looking to develop sustainable tourism strategies, differentiate their offering, and create compelling destination marketing campaigns.</p>
            </ClientCard>
            
            <ClientCard>
              <h3>Tour Operators & Experience Providers</h3>
              <p>Companies offering unique travel experiences who need help reaching their ideal customers and streamlining their digital presence and booking processes.</p>
            </ClientCard>
            
            <ClientCard>
              <h3>Travel Tech Startups</h3>
              <p>Innovative companies developing new tools for the travel industry who need strategic guidance, market positioning, and user experience optimization.</p>
            </ClientCard>
          </ClientTypes>
        </Section>
        
        <CTASection>
          <h2>Ready to Grow Your Travel Brand?</h2>
          <p>Got a project you'd like to talk about? Want to go for a run? Need fresh-brewed coffee the next time you're in Paris?</p>
          <CTAButton to="/contact">Let's Chat</CTAButton>
        </CTASection>
      </Container>
    </AboutContainer>
  );
};

export default About; 